import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePOS } from "@/context/POSContext";
import * as admin from "@/lib/adminApi";
import { itemImageSrc } from "@/lib/itemNotes";

// The photo is shrunk here before it is sent: a phone camera's 4 MB original is
// ~150 KB at this size, which every CaptainPAD downloads once and then caches.
const MAX_EDGE = 1000;
const JPEG_QUALITY = 0.82;

const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return failure?.message || "Could not reach the server";
};

/** The file as a JPEG no larger than MAX_EDGE on its long side, or the file
 * itself when this browser cannot redraw it (the server still checks it). */
export async function shrinkPhoto(file) {
  try {
    if (typeof createImageBitmap !== "function") return file;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    return blob || file;
  } catch {
    return file;
  }
}

/**
 * A menu item's photo — the top of the item details card staff open while
 * ordering. Saves on its own (one upload, one remove), like the variations.
 */
export default function MenuItemPhoto({ item, readOnly = false, onChanged }) {
  const { token } = usePOS() || {};
  const input = useRef(null);
  const [current, setCurrent] = useState(item);
  const [busy, setBusy] = useState(false);
  const src = itemImageSrc(current);

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const photo = await shrinkPhoto(file);
      const saved = await admin.uploadMenuItemImage(item.id, photo, "photo.jpg", token);
      setCurrent(saved);
      onChanged?.(saved);
      toast.success(`Photo saved for ${item.name}`);
    } catch (failure) {
      toast.error(`Photo not saved: ${errorText(failure)}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const saved = await admin.removeMenuItemImage(item.id, token);
      setCurrent(saved);
      onChanged?.(saved);
      toast.success(`Photo removed from ${item.name}`);
    } catch (failure) {
      toast.error(`Photo not removed: ${errorText(failure)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="menu-item-photo">
      <div className="w-20 h-16 rounded-md border bg-muted overflow-hidden shrink-0 flex items-center justify-center">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" data-testid="menu-item-photo-preview" />
        ) : (
          <ImagePlus className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-meta font-medium">Photo</span>
        <span className="text-micro text-muted-foreground">Shown at the top of the item's details. JPEG, PNG or WebP.</span>
        {!readOnly && (
          <div className="flex gap-2">
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="menu-item-photo-input" />
            <Button type="button" size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => input.current?.click()} data-testid="menu-item-photo-choose">
              {busy ? "Saving…" : src ? "Replace photo" : "Add photo"}
            </Button>
            {src && (
              <Button type="button" size="sm" variant="ghost" className="h-8 text-danger" disabled={busy} onClick={remove} data-testid="menu-item-photo-remove">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
